import type { InvestmentOrder } from '@/repositories/invest/InvestRepository.types';
import { type FC, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { ChevronRight, EditNote } from '@eqi-investimentos/icons/web';
import { Box, Flex, Text, Button } from '@eqi-investimentos/react';
import { salesforceOpportunityConfig } from '@/config/salesForce';
import { useSendClickEvent } from '@/modules/Customer/http/client/mutations/useSendClickEvent';
import { useInvest } from '@/contexts/InvestContext';
import { Thumb } from '@/components/atoms/Thumb';
import { CurrencyInput } from '@/components/molecules/CurrencyInput';
import { IfRender } from '@/utils/jsx';
export const SendOrderAdvisorSection: FC<SendOrderAdvisorSectionProps> = ({
  handleSelectAsset,
  handleSendOrder
}) => {
  const {
    t
  } = useTranslation('common');
  const {
    selectedAsset
  } = useInvest();
  const {
    mutate: sendClickEvent
  } = useSendClickEvent();
  const [amountToOrder, setAmountToOrder] = useState('');
  const assetTitle = selectedAsset && 'title' in selectedAsset ? selectedAsset?.title : selectedAsset?.name;
  const ticker = selectedAsset && 'ticker' in selectedAsset ? ` (${selectedAsset?.ticker})` : '';
  const sendOrder = (type: InvestmentOrder['type']) => {
    if (!selectedAsset) return;
    handleSendOrder({
      type,
      asset: selectedAsset,
      amount: amountToOrder
    });
    sendClickEvent({
      label: salesforceOpportunityConfig.sendClickEvent.invest.label(`${type === 'buy' ? 'Enviar ordem de compra' : 'Enviar ordem de venda'} - ${assetTitle}`, '/investir')
    });
  };
  return <Flex id="section-send-order-advisor" px="2xl" py="2xl" w="full" gap="l" bgColor="background.brandLight" flexDir="column" data-sentry-element="Flex" data-sentry-component="SendOrderAdvisorSection" data-sentry-source-file="SendOrderAdvisorSection.tsx">
      <Text size="2xl" color="content.primary" data-sentry-element="Text" data-sentry-source-file="SendOrderAdvisorSection.tsx">
        {t('screens.invest.sendOrderAdvisorSection.title')}
      </Text>
      <Flex gap="s" flexDir="column" data-sentry-element="Flex" data-sentry-source-file="SendOrderAdvisorSection.tsx">
        <Text size="l" color="content.primary" data-sentry-element="Text" data-sentry-source-file="SendOrderAdvisorSection.tsx">
          {t('attributes.asset')}
        </Text>
        <IfRender condition={!!assetTitle} data-sentry-element="IfRender" data-sentry-source-file="SendOrderAdvisorSection.tsx">
          <Button variant="outline" onClick={handleSelectAsset} leftIcon={<Thumb src={selectedAsset?.thumb ?? ''} width={24} height={24} fit="cover" alt="eqi-broker" />} rightIcon={<EditNote width={6} height={6} color="content.brand" />} data-sentry-element="Button" data-sentry-source-file="SendOrderAdvisorSection.tsx">
            <Text noOfLines={1} data-sentry-element="Text" data-sentry-source-file="SendOrderAdvisorSection.tsx">{`${assetTitle}${ticker}`}</Text>
          </Button>
        </IfRender>
        <IfRender condition={!assetTitle} data-sentry-element="IfRender" data-sentry-source-file="SendOrderAdvisorSection.tsx">
          <Button variant="outline" onClick={handleSelectAsset} rightIcon={<ChevronRight width={16} height={16} color="content.brand" />} data-sentry-element="Button" data-sentry-source-file="SendOrderAdvisorSection.tsx">
            {t('screens.invest.sendOrderAdvisorSection.selectAsset')}
          </Button>
        </IfRender>
      </Flex>
      <Box my="6" w="full" data-sentry-element="Box" data-sentry-source-file="SendOrderAdvisorSection.tsx">
        <Text size="l" color="content.primary" data-sentry-element="Text" data-sentry-source-file="SendOrderAdvisorSection.tsx">
          {t('attributes.value')}
        </Text>
        <CurrencyInput changeValueCallback={(value: string) => setAmountToOrder(value)} data-sentry-element="CurrencyInput" data-sentry-source-file="SendOrderAdvisorSection.tsx" />
      </Box>
      <Text size="m" color="content.tertiary" data-sentry-element="Text" data-sentry-source-file="SendOrderAdvisorSection.tsx">
        {t('screens.invest.sendOrderAdvisorSection.termsToNegotiation')}
      </Text>
      <Flex w="full" gap="m" justify="space-between" data-sentry-element="Flex" data-sentry-source-file="SendOrderAdvisorSection.tsx">
        <Button onClick={() => sendOrder('buy')} disabled={!assetTitle} textColor="content.alwaysLight" w="full" flexShrink={1} data-sentry-element="Button" data-sentry-source-file="SendOrderAdvisorSection.tsx">
          {t('screens.invest.sendOrderAdvisorSection.buy')}
        </Button>
        <Button onClick={() => sendOrder('sell')} disabled={!assetTitle} textColor="content.alwaysLight" backgroundColor="alert.error" w="full" flexShrink={1} data-sentry-element="Button" data-sentry-source-file="SendOrderAdvisorSection.tsx">
          {t('screens.invest.sendOrderAdvisorSection.sell')}
        </Button>
      </Flex>
    </Flex>;
};
type SendOrderAdvisorSectionProps = {
  handleSelectAsset: () => void;
  handleSendOrder: (props: InvestmentOrder) => void;
};